import React, { useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { recommendationApi } from '../services/api';
import SubscriptionManager from './SubscriptionManager';

const API_BASE_URL = 'http://localhost:8080';
// const API_BASE_URL = 'https://api.resources-free.com';

/**
 * RecommendationContent 组件
 * @param {Object} props
 * @param {string}   props.moduleId       - 模块ID，例如 'suck', 'wash', 'rise', 'sell'
 * @param {boolean}  props.isSubscribed   - 当前用户是否订阅了该模块的付费内容
 * @param {string}   props.date           - 当前选中的日期，格式为 'YYYY-MM-DD'
 * @param {boolean}  props.isLoggedIn     - 当前用户是否已登录
 * @param {Function} props.onLoginRequired- 当需要用户登录时的回调函数
 */
function RecommendationContent({ moduleId, isSubscribed, date, isLoggedIn, onLoginRequired }) {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);

  useEffect(() => {
    loadModuleContent(moduleId);
  }, [moduleId, isSubscribed, date]);

  const loadModuleContent = async (moduleId) => {
    setLoading(true);
    setError('');
    
    // 针对无需订阅的模块直接展示内容
    if (moduleId === 'must-see') {
      setRecommendations([{ message: '此模块无需订阅，直接显示相关内容……' }]);
      setLoading(false);
      return;
    }

    // 未订阅则显示产品信息（购买入口）
    if (!isSubscribed) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/products?module=${moduleId}`);
        if (!response.ok) throw new Error('获取产品数据失败');
        const productList = await response.json();
        setRecommendations(buildMembershipCards(productList));
      } catch (err) {
        setError(`获取产品信息失败：${err.message}`);
      }
    } else {
      try {
        // 付费用户需要携带 token 访问推荐内容接口
        const token = localStorage.getItem('token');
        if (!token) {
          setError('认证失败，请重新登录');
          if (onLoginRequired) onLoginRequired();
          setLoading(false);
          return;
        }
        // 带上 token 的请求头
        const response = await fetch(`${API_BASE_URL}/api/modules/content?module=${moduleId}&date=${date}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) throw new Error('加载内容失败');
        const contentData = await response.json();
        setRecommendations(renderContent(contentData));
      } catch (err) {
        setError('加载内容错误：' + err.message);
      }
    }
    setLoading(false);
  };

  const buildMembershipCards = (productList) => {
    return productList.map(product => ({
      type: 'membership-card',
      content: `
        <div class="membership-card">
          <div class="card-header">${product.membershipLevelId}</div>
          <div class="card-price">¥${product.price} <span class="card-period">/月</span></div>
          <div class="card-divider"></div>
          <div class="card-benefits">
            <ul>
              <li>权益 1</li>
              <li>权益 2</li>
              <li>权益 3</li>
            </ul>
          </div>
          <a href="#" class="card-button" data-product-id="${product.id}">购买</a>
        </div>
      `
    }));
  };

  const renderContent = (data) => {
    return `
      <div class="date-picker">
        <div>
          <span>选择日期：</span>
          <input type="date" id="datePicker" value="${date}" />
        </div>
        <div class="nav-buttons">
          <button id="prevDay">前一天</button>
          <button id="nextDay">后一天</button>
          <button id="today">今天</button>
        </div>
      </div>
      <div class="filter-options">
        <span>筛选：</span>
        <button class="filter-btn active" data-filter="all">全部</button>
        <button class="filter-btn" data-filter="up">涨幅</button>
        <button class="filter-btn" data-filter="industry">行业</button>
        <button class="filter-btn" data-filter="hot">热度</button>
      </div>
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>代码</th>
              <th>股票名称</th>
              <th>推荐理由</th>
              <th>推荐价格</th>
              <th>实时价格</th>

              <th>目标价</th>
              <th>止损价</th>
              <th>预期收益</th>
              <th>30日表现</th>
              <th>详情</th>
            </tr>
          </thead>
          <tbody id="stockTableBody">
            ${data.items.map(item => `
              <tr>
                <td>${item.code}</td>
                <td>${item.name}</td>
                <td>${item.reason}</td>
                <td>${item.recommendPrice}</td>
                <td>${item.realtimePrice}</td>

                <td style="color:#e74c3c;">${item.targetPrice}</td>
                <td style="color:#e74c3c;">${item.stopPrice}</td>
                <td style="color:#27ae60;">${item.expectedYield}</td>
                <td>
                  <div class="performance-bar">
                    <div class="fill" style="width:${item.performance}%"></div>
                  </div>
                </td>
                <td>
                  <button class="detail-btn" style="background:#f39c12;color:#2c3e50;border:none;padding:5px 10px;border-radius:4px;cursor:pointer;">查看</button>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  };


  return (
    <div className="recommendation-content">
      {loading && <div>加载中...</div>}
      {error && <div className="error">{error}</div>}
      <div dangerouslySetInnerHTML={{ __html: recommendations }} />
      {showQRCode && paymentInfo && (
        <div id="paymentSection">
          <h3>请扫码支付</h3>
          <QRCodeCanvas value={paymentInfo.url} size={200} />
          <p>完成支付后系统将自动更新状态</p>
        </div>
      )}
      {!isSubscribed && (
        <SubscriptionManager 
          moduleId={moduleId} 
          onSubscriptionSuccess={() => {
            loadModuleContent(moduleId);
          }} 
        />
      )}
    </div>
  );
}

export default RecommendationContent;
